import { useState, type ReactNode } from 'react';
import { api } from '../../lib/api.js';
import { useQuery } from '../../lib/useQuery.js';
import { Page } from '../../app/AppShell.js';
import { Modal } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';
import { Loading, ErrorNote, EmptyState } from '../../components/ui.js';
import { absoluteTime } from '../../lib/format.js';
import type { PortableLicense } from '../../lib/types.js';

/**
 * Operator owners and admins: mint licence keys for the portable CMS player app
 * (the same keys as the portable repo's `tools/license/generate_license_key.sh`).
 * A key for a player some customer has linked is filed under that customer, so
 * their portable CMS hands it to the player; any other key is pasted in through
 * the player's QR form. The player checks keys offline, so one cannot be revoked.
 */
export function PortableLicensesScreen(): ReactNode {
  const licenses = useQuery(() => api.portableLicenses(), []);
  const [creating, setCreating] = useState(false);
  const [shown, setShown] = useState<PortableLicense[] | null>(null);

  return (
    <Page
      title="Portable licences"
      actions={
        <button className="btn primary" onClick={() => setCreating(true)}>
          Issue licence
        </button>
      }
    >
      <p className="muted small" style={{ marginBottom: 12 }}>
        Licence keys for the portable CMS player app. Each key unlocks only the players whose licence
        ID (<code>XXXX-XXXX-XXXX</code>, on the player's licence screen) it lists. A player linked to a
        customer's account gets its key on its next sync with their portable CMS; any other player
        needs the key pasted in through the QR code on its licence screen. Players verify keys offline,
        so a key cannot be revoked once it has been issued.
      </p>

      {licenses.loading && !licenses.data ? <Loading what="licences" /> : null}
      {licenses.error ? <ErrorNote error={licenses.error} /> : null}

      {licenses.data ? (
        licenses.data.length === 0 ? (
          <EmptyState>No licences issued yet.</EmptyState>
        ) : (
          <div className="card">
            <table className="grid">
              <thead>
                <tr>
                  <th>Licensee</th>
                  <th>Customer</th>
                  <th>Licence IDs</th>
                  <th>Issued</th>
                  <th>Expires</th>
                  <th>By</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {licenses.data.map((l) => (
                  <tr key={l.id}>
                    <td>{l.licensee}</td>
                    <td className="small">{l.tenantName ?? <span className="muted">—</span>}</td>
                    <td className="mono small">{l.licenseIds.join(', ')}</td>
                    <td className="muted small">{absoluteTime(l.issuedAt)}</td>
                    <td className="muted small">{l.expiresAt ? absoluteTime(l.expiresAt) : 'never'}</td>
                    <td className="muted small">{l.issuedBy?.name ?? '—'}</td>
                    <td>
                      <button className="btn small" onClick={() => setShown([l])}>
                        Show key
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {creating ? (
        <IssueLicenseModal
          onClose={() => setCreating(false)}
          onIssued={(issued) => {
            setCreating(false);
            setShown(issued);
            licenses.refetch();
          }}
        />
      ) : null}

      {shown ? <KeysModal licenses={shown} onClose={() => setShown(null)} /> : null}
    </Page>
  );
}

export function IssueLicenseModal({
  onClose,
  onIssued,
}: {
  onClose: () => void;
  onIssued: (licenses: PortableLicense[]) => void;
}): ReactNode {
  const toast = useToast();
  const [licensee, setLicensee] = useState('');
  const [idsText, setIdsText] = useState('');
  const [perpetual, setPerpetual] = useState(true);
  const [days, setDays] = useState('365');
  const [busy, setBusy] = useState(false);

  // One ID per line or comma-separated; spaces inside an ID are tolerated server-side.
  const licenseIds = idsText
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter((s) => s !== '');
  const expiresInDays = perpetual ? null : Number.parseInt(days, 10);
  const valid =
    licensee.trim() !== '' &&
    !licensee.includes('|') &&
    licenseIds.length > 0 &&
    (expiresInDays === null || (Number.isInteger(expiresInDays) && expiresInDays > 0));

  async function submit(): Promise<void> {
    setBusy(true);
    try {
      onIssued(await api.issuePortableLicense({ licensee: licensee.trim(), licenseIds, expiresInDays }));
    } catch (err) {
      toast.error(err);
      setBusy(false);
    }
  }

  return (
    <Modal title="Issue portable licence" onClose={onClose}>
      <div className="stack">
        <label className="field">
          Licensee
          <input
            value={licensee}
            onChange={(e) => setLicensee(e.target.value)}
            placeholder="Customer / site name"
            maxLength={120}
            autoFocus
          />
        </label>

        <label className="field">
          Licence IDs ({licenseIds.length})
          <textarea
            className="mono"
            rows={5}
            value={idsText}
            onChange={(e) => setIdsText(e.target.value)}
            placeholder={'ABCD-EFGH-JKMN\nPQRS-TVWX-YZ01'}
          />
          <span className="muted small">One per line (or comma-separated), from each player's licence screen.</span>
        </label>

        <div className="field">
          Expiry
          <label className="row" style={{ gap: 6 }}>
            <input type="radio" checked={perpetual} onChange={() => setPerpetual(true)} />
            Perpetual
          </label>
          <label className="row" style={{ gap: 6 }}>
            <input type="radio" checked={!perpetual} onChange={() => setPerpetual(false)} />
            Expires after
            <input
              type="number"
              min={1}
              max={36500}
              style={{ width: 90 }}
              value={days}
              disabled={perpetual}
              onChange={(e) => setDays(e.target.value)}
            />
            days
          </label>
        </div>

        <div className="row between">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || !valid} onClick={() => void submit()}>
            {busy ? 'Issuing…' : 'Issue licence'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** One key, or every key one issue produced (one per customer, plus one for unlinked players). */
export function KeysModal({ licenses, onClose }: { licenses: PortableLicense[]; onClose: () => void }): ReactNode {
  const toast = useToast();
  const title = licenses.length === 1 ? `Licence key — ${licenses[0]?.licensee ?? ''}` : `${licenses.length} licence keys`;

  return (
    <Modal title={title} onClose={onClose}>
      <div className="stack">
        {licenses.map((license) => (
          <div key={license.id} className="stack" style={{ gap: 6 }}>
            <p className="muted small" style={{ margin: 0 }}>
              <span className="mono">{license.licenseIds.join(', ')}</span>
              {license.expiresAt ? ` · until ${absoluteTime(license.expiresAt)}` : ' · no expiry'}
            </p>
            <p className="small" style={{ margin: 0 }}>
              {license.delivery === 'cms'
                ? `Unlocks on the player's next sync with ${license.tenantName ?? 'your'} portable CMS. Nothing to send.`
                : 'Not linked to any account yet. On the player, open the licence screen, scan its QR code with a phone and paste this key.'}
            </p>
            <div className="mono small" style={{ wordBreak: 'break-all', padding: '6px 0' }}>
              {license.licenseKey}
            </div>
            <div>
              <button
                className="btn small"
                onClick={() => {
                  void navigator.clipboard?.writeText(license.licenseKey).then(() => toast.push('Copied'));
                }}
              >
                Copy
              </button>
            </div>
          </div>
        ))}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="btn primary small" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}
